import { useState } from 'react';
import { Send } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

type TargetAudience = 'all' | 'free' | 'monthly' | 'annual' | 'inactive_30d';

const AUDIENCE_LABELS: Record<TargetAudience, string> = {
  all:          'Todos os usuários',
  free:         'Plano Grátis',
  monthly:      'Plano Mensal',
  annual:       'Plano Anual',
  inactive_30d: 'Inativos há 30+ dias',
};

const TEMPLATES = [
  {
    title: 'Reengajamento',
    body:  'Suas estrelas têm mensagens esperando por você ✦ Que tal fazer uma nova leitura hoje?',
  },
  {
    title: 'Alinhamento planetário',
    body:  'As energias cósmicas estão favoráveis para você esta semana 🌟 Consulte seu mapa astral!',
  },
  {
    title: 'Nova funcionalidade',
    body:  'Novidade no Professya! Explore o Atlas Astral e descubra padrões únicos do seu mapa ✨',
  },
];

export function AdminNotificationsPage() {
  const { profile }     = useAuth();
  const [title,    setTitle]    = useState('');
  const [body,     setBody]     = useState('');
  const [audience, setAudience] = useState<TargetAudience>('all');
  const [url,      setUrl]      = useState('');
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState<{ count: number; ts: string } | null>(null);

  async function send() {
    if (!title.trim() || !body.trim()) return;
    setSending(true);
    const { data } = await supabase.functions.invoke('admin-send-notification', {
      body: { title, body, audience, url: url || null, adminId: profile?.id },
    });
    setSending(false);
    if (data?.data?.count !== undefined) {
      setSent({ count: data.data.count, ts: new Date().toLocaleString('pt-BR') });
      setTitle(''); setBody(''); setUrl('');
    }
  }

  return (
    <div>
      <AdminPageHeader title="Notificações Push" objective="Envie notificações para os celulares dos usuários. Push notifications exigem que o usuário tenha aceito receber notificações no app ou navegador. Use com moderação, excesso de notificações leva ao bloqueio pelo usuário." />

      {sent && (
        <div style={{ padding: '1rem', backgroundColor: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#4ade80', fontSize: 'var(--text-label)' }}>
            ✓ Notificação enviada para <strong>{sent.count.toLocaleString('pt-BR')}</strong> usuário(s) às {sent.ts}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setSent(null)}>✕</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1.5rem', alignItems: 'start' }}>
        {/* Compose */}
        <div className="card">
          <h2 style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1.25rem' }}>Criar notificação</h2>

          <div className="form-group">
            <label className="form-label">Público-alvo</label>
            <select className="form-select" value={audience} onChange={e => setAudience(e.target.value as TargetAudience)} title="Audiência: grupo de usuários que receberão a notificação. 'Inativos 30+ dias' = usuários que não acessam há mais de 30 dias.">
              {(Object.keys(AUDIENCE_LABELS) as TargetAudience[]).map(k => (
                <option key={k} value={k}>{AUDIENCE_LABELS[k]}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Título <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({title.length}/65)</span></label>
            <input type="text" className="form-input" value={title} onChange={e => setTitle(e.target.value.slice(0, 65))} placeholder="Ex: ✦ Novidades no Professya" />
          </div>

          <div className="form-group">
            <label className="form-label">Corpo <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({body.length}/200)</span></label>
            <textarea className="form-textarea" value={body} onChange={e => setBody(e.target.value.slice(0, 200))} rows={3} placeholder="Mensagem da notificação..." />
          </div>

          <div className="form-group">
            <label className="form-label">URL de destino (opcional)</label>
            <input type="text" className="form-input" value={url} onChange={e => setUrl(e.target.value)} placeholder="/forecast" />
          </div>

          {/* Preview */}
          {title && (
            <div style={{ marginBottom: '1rem', padding: '0.875rem', backgroundColor: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
              <p style={{ fontSize: '11px', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.4rem' }}>Preview</p>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '18px' }}>✦</div>
                <div>
                  <p style={{ margin: '0 0 0.2rem', fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{title || 'Título'}</p>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{body || 'Corpo da mensagem'}</p>
                </div>
              </div>
            </div>
          )}

          <div style={{padding:'0.6rem 0.875rem', backgroundColor:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.2)', borderRadius:'6px', fontSize:'var(--text-caption)', color:'#fbbf24', marginBottom:'1rem'}}>
            ⚠️ Notificações são enviadas apenas para usuários que autorizaram recebê-las. Usuários que bloquearam notificações não serão impactados.
          </div>

          <button className="btn btn-primary btn-sm" onClick={send} disabled={sending || !title.trim() || !body.trim()}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Send size={14} /> {sending ? 'Enviando...' : `Enviar para ${AUDIENCE_LABELS[audience]}`}
          </button>
        </div>

        {/* Templates */}
        <div>
          <h2 style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Modelos rápidos</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {TEMPLATES.map(t => (
              <button key={t.title} onClick={() => { setTitle(t.title); setBody(t.body); }}
                className="card" style={{ textAlign: 'left', cursor: 'pointer', padding: '0.875rem', border: 'none', background: 'none', width: '100%' }}>
                <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.3rem', fontSize: 'var(--text-label)' }}>{t.title}</p>
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.body}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
